import { useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Aks2talOnly } from 'components/AdminOnly';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { AppState } from 'store';
import {
  getSensorInventory,
  saveSensorInventory,
  LinkedInventoryItem,
} from 'modules/inventory/sensorInventory';

interface Props {
  orgId: string;
  sensorId: string;
}
const label = (item: LinkedInventoryItem) =>
  [item.internalId, item.name, item.identifier, item.iccid, item.phoneNumber]
    .filter(Boolean)
    .join(' · ') || item.id;
function Item({ item, canEdit }: { item: LinkedInventoryItem; canEdit: boolean }) {
  return (
    <div className="rounded-md border bg-white p-3">
      <p className="font-medium">
        {item.type === 'simcards' ? 'SIM-kort' : 'Lagerdel'} ·{' '}
        {item.name || item.identifier || item.iccid || item.id}
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 break-all">
        <dt>Internt ID</dt>
        <dd>{item.internalId || 'Ikke registreret'}</dd>
        {item.identifier && (
          <>
            <dt>Enheds-ID</dt>
            <dd>{item.identifier}</dd>
          </>
        )}
        {item.type === 'simcards' && (
          <>
            <dt>ICCID</dt>
            <dd>{item.iccid || 'Ikke registreret'}</dd>
            <dt>Mobilnummer</dt>
            <dd>{item.phoneNumber || 'Ikke registreret'}</dd>
          </>
        )}
        <dt>Kunde i lager</dt>
        <dd>{item.orgTitle || item.orgId || 'Ikke tilknyttet'}</dd>
      </dl>
      {canEdit && (
        <Link
          className="text-sky-700 underline"
          to={`/inventory/${encodeURIComponent(item.type)}?id=${encodeURIComponent(item.id)}`}
        >
          Åbn i lager
        </Link>
      )}
    </div>
  );
}
function Content({ orgId, sensorId }: Props) {
  const uid = useSelector((state: AppState) => state.user.details.uid);
  const query = useQuery({
    queryKey: ['sensor-inventory', uid, orgId, sensorId],
    queryFn: () => getSensorInventory(orgId, sensorId),
    staleTime: 0,
  });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const searchId = useId();
  const selectId = useId();
  const data = query.data;
  if (!data)
    return (
      <div className="p-4">
        {query.isError ? (
          <>
            <p role="alert">Lagerkoblingen kunne ikke hentes.</p>
            <Button variant="outline" onClick={() => void query.refetch()}>
              Prøv igen
            </Button>
          </>
        ) : (
          <p role="status">Henter lagerkobling…</p>
        )}
      </div>
    );
  const value = selected ?? data.inventoryId ?? '';
  const choices = data.choices.filter(
    (item) =>
      item.id === value ||
      label(item).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const suggestions =
    !data.inventoryId && data.deviceId
      ? data.choices.filter(
          (item) => item.identifier === data.deviceId || item.internalId === data.deviceId
        )
      : [];
  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    setFeedback('');
    try {
      await saveSensorInventory(orgId, sensorId, value || null, data.version);
      setSelected(value);
      setFeedback(
        value
          ? 'Kobling og kundetilknytning er gemt.'
          : 'Koblingen er fjernet. Kundetilknytningen i lageret er bevaret.'
      );
      const refreshed = await query.refetch();
      if (refreshed.isSuccess) setSelected(undefined);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Koblingen kunne ikke gemmes. Prøv igen.'
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3 border-t p-4 text-sm" aria-busy={saving}>
      {query.isError && (
        <p role="alert">Opdateringen mislykkedes. Oplysningerne kan være forældede.</p>
      )}
      {data.warnings.length > 0 && (
        <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="font-medium">Kontrollér koblingen</p>
          <ul className="list-disc pl-5">
            {data.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      {data.item ? (
        <Item item={data.item} canEdit={data.canEdit} />
      ) : (
        <p>Ingen lagerdel eller SIM er knyttet til denne sensor.</p>
      )}
      {data.sim && <Item item={data.sim} canEdit={data.canEdit} />}
      {data.item && data.item.type !== 'simcards' && !data.sim && (
        <p>Intet SIM er registreret via denne lagerdel.</p>
      )}
      {data.canEdit && (
        <fieldset disabled={saving || query.isFetching} className="space-y-2">
          <legend className="font-medium">Tilknyt lagerdel eller SIM</legend>
          <p>
            Gem opdaterer også lagerdelens og SIM-kortets kunde til sensorens kunde. Vælg udstyret,
            hvis SIM-kortet allerede er knyttet til udstyr.
          </p>
          {suggestions.length > 0 && (
            <p>
              Mulige match på enheds-ID — kontrollér før tilknytning:{' '}
              {suggestions.map(label).join('; ')}
            </p>
          )}
          <label htmlFor={searchId}>Søg på internt ID, enheds-ID, ICCID eller mobilnummer</label>
          <Input id={searchId} value={search} onChange={(event) => setSearch(event.target.value)} />
          <label htmlFor={selectId} className="block">
            Lagerdel eller SIM
          </label>
          <select
            id={selectId}
            className="w-full rounded-md border bg-white p-2"
            value={value}
            onChange={(event) => {
              setSelected(event.target.value);
              setFeedback('');
            }}
          >
            <option value="">Ingen kobling</option>
            {choices.map((item) => (
              <option key={item.id} value={item.id}>
                {label(item)}
                {item.orgTitle ? ` — ${item.orgTitle}` : ''}
              </option>
            ))}
          </select>
          {choices.length === 0 && <p>Ingen lagerdele matcher søgningen.</p>}
          <Button onClick={() => void save()} disabled={saving || query.isFetching}>
            {saving ? 'Gemmer…' : 'Gem kobling og kunde'}
          </Button>
        </fieldset>
      )}
      {error && <p role="alert">{error}</p>}
      {feedback && <p role="status">{feedback}</p>}
      <Button
        variant="outline"
        disabled={saving || query.isFetching}
        onClick={() => void query.refetch()}
      >
        Opdatér lageroplysninger
      </Button>
    </div>
  );
}
function Section(props: Props) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return (
    <section className="my-4 rounded-lg border border-slate-200 bg-slate-50">
      <button
        type="button"
        className="w-full rounded-lg px-4 py-3 text-left text-sm font-medium"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(!open)}
      >
        AKS intern · Lager og SIM {open ? '▴' : '▾'}
      </button>
      <div id={contentId}>{open && <Content {...props} />}</div>
    </section>
  );
}
export default function SensorInventory(props: Props) {
  return (
    <Aks2talOnly>
      <Section key={`${props.orgId}/${props.sensorId}`} {...props} />
    </Aks2talOnly>
  );
}
